import React, { useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  Platform,
  Linking,
  TouchableOpacity,
  Animated,
} from "react-native";
import { StatusBar } from "expo-status-bar";
import Header from "../components/Header";
import { useTheme } from "../contexts/ThemeContext";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation";
import {
  updateDocumentTitle,
  updateMetaTag,
  updateCanonicalLink,
} from "../utils/seoUtils";

const LetterScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  // Animation refs
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;

  // Theme-aware colors
  const bgColor = theme === "dark" ? "#18181B" : "#FAFAFA";
  const cardColor = theme === "dark" ? "#232327" : "#FFFFFF";
  const textColor = theme === "dark" ? "#F2F2F2" : "#18181B";
  const subTextColor = theme === "dark" ? "#C2C2C2" : "#52525B";
  const linkColor = theme === "dark" ? "#60A5FA" : "#2563EB";

  useEffect(() => {
    if (Platform.OS === "web") {
      updateDocumentTitle("Letter to the Community - BC STEM");
      updateMetaTag("description", "A letter from the BC STEM team celebrating our 3,500 user milestone and sharing our vision for the future of STEM education in British Columbia.");
      updateCanonicalLink("https://bcstem.ca/letter");
    }

    // Animate letter entrance
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  const handleEmailPress = () => {
    Linking.openURL("mailto:nonit.gupta@bcstem.ca");
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: bgColor }]}>
      <StatusBar style={theme === "dark" ? "light" : "dark"} />
      
      <Header
        onInfoPress={() => navigation.navigate("About")}
      />

      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Animated.View style={[
          styles.letterCard, 
          { 
            backgroundColor: cardColor,
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          }
        ]}>
          <View style={styles.letterHeader}>
            <Text style={[styles.title, { color: textColor }]}>
              Letter to the Community
            </Text>
            <Text style={[styles.subtitle, { color: subTextColor }]}>
              Celebrating 3,500 Users & Our Journey Ahead
            </Text>
          </View>

          <View style={styles.letterContent}>
            <Text style={[styles.paragraph, { color: textColor }]}>
              We built{" "}
              <Text style={[styles.highlight, { color: linkColor }]}>bcstem.ca</Text>{" "}
              with the goal of empowering students and elevating STEM in British Columbia, 
              giving them access to the kinds of opportunities we wished we had when we were 
              first exploring and growing in the STEM field. Seeing our platform reach over{" "}
              <Text style={[styles.highlight, { color: linkColor }]}>3,000 students</Text>{" "}
              this year and knowing it has made a tangible impact through the incredible 
              feedback and support from the community has been truly rewarding.
            </Text>

            <Text style={[styles.paragraph, { color: textColor }]}>
              Today, bcstem.ca features{" "}
              <Text style={[styles.highlight, { color: linkColor }]}>over 100 opportunities</Text>{" "}
              across British Columbia, and we have already formed partnerships with more than five nonprofits in BC and two in Ontario. 
              Looking ahead, we aim to keep improving our platform, evolving it from a simple 
              database into a thriving community that empowers and connects the next generation 
              of STEM leaders in BC.
            </Text>

            <Text style={[styles.paragraph, { color: textColor }]}>
              We invite you to join us in shaping a future where every student, no matter 
              how busy or where they come from, has the chance to explore STEM. In this future,{" "}
              <Text style={[styles.highlight, { color: linkColor }]}>
                success is determined by passion and hard work, not by pedigree.
              </Text>
            </Text>

            <Text style={[styles.paragraph, { color: textColor }]}>
              If you would like to collaborate, share opportunities, or simply learn more, 
              please feel free to reach out to us anytime at{" "}
              <TouchableOpacity onPress={handleEmailPress} style={styles.emailLink}>
                <Text style={[styles.emailText, { color: linkColor }]}>
                  nonit.gupta@bcstem.ca
                </Text>
              </TouchableOpacity>
              . We would be more than happy to connect.
            </Text>
          </View>

          <View style={styles.signature}>
            <Text style={[styles.signatureText, { color: subTextColor }]}>
              — The BC STEM Team
            </Text>
          </View>
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  letterCard: {
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
    borderRadius: 20,
    padding: Platform.OS === "web" ? 48 : 32,
    marginHorizontal: Platform.OS === "web" ? 0 : 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 8,
  },
  letterHeader: {
    marginBottom: 32,
    alignItems: "center",
  },
  title: {
    fontSize: Platform.OS === "web" ? 36 : 30,
    fontWeight: "700",
    fontFamily: "Inter-Bold",
    textAlign: "center",
    marginBottom: 12,
    letterSpacing: -0.8,
  },
  subtitle: {
    fontSize: Platform.OS === "web" ? 20 : 18,
    fontWeight: "500",
    fontFamily: "Inter-Medium",
    textAlign: "center",
    lineHeight: Platform.OS === "web" ? 28 : 26,
  },
  letterContent: {
    marginBottom: 32,
  },
  paragraph: {
    fontSize: Platform.OS === "web" ? 18 : 16,
    lineHeight: Platform.OS === "web" ? 32 : 28,
    fontFamily: "Inter-Regular",
    marginBottom: 24,
    textAlign: "left",
  },
  highlight: {
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
  },
  emailLink: {
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 4,
  },
  emailText: {
    fontSize: Platform.OS === "web" ? 18 : 16,
    fontWeight: "600",
    fontFamily: "Inter-SemiBold",
    textDecorationLine: "underline",
  },
  signature: {
    alignItems: "center",
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: "rgba(128,128,128,0.2)",
  },
  signatureText: {
    fontSize: Platform.OS === "web" ? 18 : 16,
    fontStyle: "italic",
    fontFamily: "Inter-Medium",
    letterSpacing: 0.3,
  },
});

export default LetterScreen;
